// Controlled duration input for time entries. Value/onChange speak the
// decimal-hours string form ("1.10" = 1h 6m) that the `hours` contract
// uses everywhere, so it drops into forms wherever a free-text hours input
// lived. The +/- buttons step by 6 minutes; typing snaps to the nearest
// tenth on blur. A "1h 6m" caption makes the decimal legible.
//
// Mirrors the controlled-input conventions of DateInput/PhoneInput.

import { useEffect, useRef, useState } from 'react';
import {
  roundToTenthHours,
  fmtDuration,
  TENTH_HOURS,
  MAX_HOURS,
} from '../utils/duration';
import './DurationStepper.css';

export interface DurationStepperProps {
  /** Current value as a decimal-hours string, e.g. "1.25". */
  value: string;
  /** Called with a snapped, clamped 2-dp string on every change. */
  onChange: (next: string) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /** Native id for label association. */
  id?: string;
  className?: string;
}

export function DurationStepper(props: DurationStepperProps): JSX.Element {
  const {
    value,
    onChange,
    min = TENTH_HOURS,
    max = MAX_HOURS,
    step = TENTH_HOURS,
    disabled = false,
    id,
    className,
  } = props;

  // Local typing buffer so free typing isn't re-snapped mid-keystroke.
  // Re-sync from `value` whenever the prop changes and we're not focused —
  // this also preserves a seeded historical non-quarter value ("0.30")
  // until the user actually interacts.
  const [buf, setBuf] = useState(value);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setBuf(value);
  }, [value]);

  const numeric = Number(value);
  const atMin = Number.isFinite(numeric) && numeric <= min;
  const atMax = Number.isFinite(numeric) && numeric >= max;

  const bump = (delta: number): void => {
    const base = Number.isFinite(numeric) ? numeric : min;
    const next = roundToTenthHours(base + delta, min, max);
    setBuf(next);
    onChange(next);
  };

  const commit = (): void => {
    // An empty box stays empty. Without this, blurring one snaps it to the
    // minimum — `Number('')` is 0 and roundToTenthHours clamps that up to
    // TENTH_HOURS — so merely tabbing through a duration nobody typed would
    // write 0.10 into it. Quick add opens with this field blank on purpose.
    if (buf.trim() === '') {
      if (value !== '') onChange('');
      return;
    }
    const next = roundToTenthHours(Number(buf), min, max);
    setBuf(next);
    // Blur alone is not an edit. Reporting one anyway is what let merely
    // tabbing through this box drive the triad's arithmetic, and a host form
    // watching for changes would see a dirty field nobody touched.
    if (next !== value) onChange(next);
  };

  return (
    <div className={`duration-stepper ${className ?? ''}`}>
      <div className="duration-stepper-controls">
        <button
          type="button"
          className="duration-stepper-btn"
          onClick={() => bump(-step)}
          disabled={disabled || atMin}
          aria-label="Decrease by 6 minutes"
        >
          −
        </button>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          className="form-input duration-stepper-input"
          value={buf}
          disabled={disabled}
          onChange={(e) => setBuf(e.target.value)}
          onFocus={() => {
            focused.current = true;
          }}
          onBlur={() => {
            focused.current = false;
            commit();
          }}
        />
        <button
          type="button"
          className="duration-stepper-btn"
          onClick={() => bump(step)}
          disabled={disabled || atMax}
          aria-label="Increase by 6 minutes"
        >
          +
        </button>
      </div>
      {/* No caption under an empty box — fmtDuration('') reads "0m", which
          asserts a duration of zero rather than none. */}
      {value.trim() !== '' && (
        <span className="duration-stepper-label">{fmtDuration(value)}</span>
      )}
    </div>
  );
}
